import PropTypes from 'prop-types'
import styled from 'styled-components'
import { t, jt } from 'ttag'
import { useDispatch } from 'react-redux'

import { features } from '@h2/components/media'
import { WHITE, BLACK, PB30T, PG90T, SLB90T, SG } from '@h2/components/colors'
import { Flex } from '@h2/components/layout/flex'
import { Box } from '@h2/components/layout/box'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Checklist } from '@h2/components/containers/checklist'
import { PromoButton } from '@h2/components/buttons/promo'
import { Button } from '@h2/components/buttons/button'
import { Icon } from '@h2/components/display/icon'
import explore from '@h2/assets/svg/illustrations/source/explore.svg'
import educator from '@h2/assets/svg/home/educatorDarkGreen.svg'
import { Panel } from '@h2/common/components/panel'
import { openSigninDialog } from '@main/actions/signinOrRegisterDialog'

const createToLearn = <span key="create">{t`Create an account to learn`}</span>
const who = <span key="who">{t`who you are`}</span>
const what = <span key="what">{t`what you are`}</span>
const interested = <span key="interested">{t`interested in doing`}</span>
const workingWithOthers = <span key="others">{t`working with others`}</span>
const accountFeatures = [
  t`Learn which careers are a fit for you`,
  t`Discover the next steps to pursuing a job, college or opportunity`,
  t`Organize your activities to keep you on track`
]
const SignInText = styled(Text)`
  letter-spacing: 0.5px;
  margin-top: 10px;
`
const WhiteBox = styled(Box)`
  padding: 15px;
  background-color: ${WHITE};
  background-image: url('${educator}');
  background-repeat: no-repeat;
  background-position: bottom 15px right 0;
  background-size: 152px auto;

  @media (${features.minWidth.desktop}) {
    background-position: bottom 0 right -5px;
    background-size: 133px auto;

    div {
      width: 100%;
    }

    h3 {
      margin-bottom: 5px;
    }

    p {
      font-size: 18px;
      color: ${BLACK};
    }
  }
`
const InnerFlex = styled(Flex)`
  flex-direction: column;

  @media (${features.minWidth.desktop}) {
    flex-direction: row;
    align-items: center;
    padding-bottom: 5px;

    h3 {
      margin-top: 0;
    }

    > div:nth-child(2) {
      margin-left: 16px;
    }

    @media (${features.maxWidth.widescreen}) {
      flex-direction: column;

      + div {
        width: 180%;
        margin-top: 15px;
      }

      > div:first-child {
        margin-bottom: 15px;
      }
    }
  }
`
const CallToAction = styled(Box)`
  position: absolute;
  right: 0;
  bottom: 20px;
  background-color: ${WHITE};
  border: 1px solid #ededed;
  border-radius: 5px;
  padding: 40px 30px;
  width: 100%;

  h3 {
    font-size: clamp(42px, 4vw, 48px);
    line-height: 0.75;
    margin-bottom: 28px;
    white-space: nowrap;

    > span {
      display: block;
      margin-bottom: 16px;
    }

    > span:last-child {
      font-size: clamp(53px, 5vw, 60px);
      margin-bottom: 0;
    }

    > span:nth-child(2) {
      color: ${SG};
      font-size: clamp(84px, 8vw, 96px);
      margin-bottom: 12px;
    }

    > span:nth-child(3) {
      color: ${SG};
      font-size: clamp(110px, 10vw, 126px);

      span:first-child {
        color: ${BLACK};
        font-size: clamp(21px, 2vw, 24px);
        writing-mode: vertical-lr;
        transform: rotate(180deg);
      }
    }
  }

  > button {
    font-size: 26px;
    border-radius: 6px;
    border-width: 2px;

    &:hover {
      border-width: 2px;
    }
  }
`
const ActionWrap = styled(Box)`
  display: none;
  position: relative;
  width: 100%;
  max-width: 338px;
  min-width: 279px;
  margin-left: 20px;

  @media (${features.minWidth.desktop}) {
    display: flex;

    @media (${features.maxWidth.widescreen}) {
      flex-direction: column;
      max-width: none;

      ${CallToAction} {
        position: relative;
        top: 15px;
        padding: 30px 20px;

        h3 {
          font-size: clamp(42px, 6vw, 58px);
          margin-bottom: 20px;

          > span {
            margin-bottom: 14px;
          }

          > span:last-child {
            font-size: clamp(58px, 8vw, 76px);
          }

          > span:nth-child(2) {
            font-size: clamp(94px, 11vw, 114px);
          }

          > span:nth-child(3) {
            font-size: clamp(120px, 15vw, 154px);

            span:first-child {
              font-size: clamp(18px, 3vw, 30px);
            }
          }
        }
      }
    }
  }
`
const FlexWrap = styled(Flex)`
  @media (${features.minWidth.desktop}) {
    flex-direction: row;
  }
`
const CreateAnAccount = ({ systemName, tenantId }) => {
  const dispatch = useDispatch()
  const signIn = (
    <Button key="signin" variant="link" onClick={() => dispatch(openSigninDialog())}>
      {t`Sign in here`}
    </Button>
  )

  return (
    <Panel color={tenantId === 'california' ? SLB90T : PG90T}>
      <FlexWrap direction="column">
        <Flex direction="column">
          <InnerFlex px="5px" py="20px">
            <Box margin="5px 0 0 0">
              <Icon
                isCentered
                label="man hiking in mountains"
                width="318px"
                height="auto"
                dangerouslySetGlyph={explore}
              />
            </Box>
            <Box>
              <Heading
                level="3"
                weight="light"
                variant="mixed-weight"
                mt="25px"
                mb="25px">
                {jt`${createToLearn} more about ${who} and ${what} ${interested}. The ${systemName} can help you:`}
              </Heading>
              <Box px="10px">
                <Checklist>
                  {accountFeatures.map((item, idx) => (
                    <Checklist.Item key={idx}>{item}</Checklist.Item>
                  ))}
                </Checklist>
              </Box>
            </Box>
          </InnerFlex>
          <Box padding="10px 5px 20px 5px">
            <WhiteBox>
              <Heading level="3" weight="light" variant="mixed-weight">
                {jt`When ${workingWithOthers}`}
              </Heading>
              <Box width="55%">
                <Text color={PB30T} size="medium">
                  {t`Your account will also let you monitor, collaborate, and report on their progress`}
                </Text>
              </Box>
            </WhiteBox>
          </Box>
        </Flex>
        <ActionWrap>
          <CallToAction>
            <Heading level="3" transform="uppercase" align="center">
              <span>{t`Get started`}</span>
              <span>{t`today`}</span>
              <span>
                <span>{t`with a`}</span>
                <span>{t`free`}</span>
              </span>
              <span>{t`account`}</span>
            </Heading>
            <PromoButton
              isFullWidth
              align="center"
              onClick={() => dispatch(openSigninDialog('createAccount'))}>
              {t`Register Now`}
            </PromoButton>
            <SignInText size="medium" weight="bold" align="center">
              {jt`Already have an account? ${signIn}`}
            </SignInText>
          </CallToAction>
        </ActionWrap>
      </FlexWrap>
    </Panel>
  )
}

CreateAnAccount.propTypes = {
  systemName: PropTypes.string.isRequired,
  tenantId: PropTypes.string.isRequired
}

export { CreateAnAccount }
